import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ArrowRight, FileStack, LayoutDashboard, PencilRuler, Sparkles, Upload, Handshake } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { QuoteShowcase } from "@/components/quote-showcase";
import { getQuotaInfo, type QuotaInfo } from "@/lib/quota-banners";

const METHODS = [
  {
    id: "auto",
    n: "1",
    icon: Sparkles,
    title: "Smart Auto Quote",
    tag: "Let it do the hard work",
    body: "Our main feature and the quickest way to get a quote started. Upload drawings, photos or notes, or simply describe the job. The software helps identify the work involved and puts together a detailed draft quote in minutes. You then check what's included, add anything missing and adjust your prices before finalising it.",
    tip: "Clear drawings, measurements and job details help you get a more accurate quote.",
    featured: true,
  },
  {
    id: "template",
    n: "2",
    icon: FileStack,
    title: "Template Quote",
    tag: "Quick and consistent pricing",
    body: "Ideal for jobs you price regularly. Start with a ready-made template, then adjust it to suit the job. It saves time and helps keep your pricing consistent.",
  },
  {
    id: "manual",
    n: "3",
    icon: PencilRuler,
    title: "Manual Quote",
    tag: "You're in control",
    body: "Start with a blank quote and add each work item yourself. A good fit when you want complete control over the work included, quantities and prices.",
  },
] as const;

const LINKS = [
  { href: "/quotes/new", label: "Start Smart Auto Quote", icon: Upload, testId: "link-start-auto-quote", primary: true },
  { href: "/quotes/template", label: "Start Template Quote", icon: FileStack, testId: "link-start-template-quote" },
  { href: "/quotes/manual", label: "Start Manual Quote", icon: PencilRuler, testId: "link-start-manual-quote" },
  { href: "/dashboard", label: "Explore Dashboard", icon: LayoutDashboard, testId: "link-explore-dashboard" },
];

function plural(n: number) {
  return `${n} free quote${n === 1 ? "" : "s"}`;
}

export interface QuoteWelcomeContentProps {
  quotaInfo?: QuotaInfo | null;
  firstName?: string | null;
}

export function QuoteWelcomeContent({ quotaInfo = null, firstName }: QuoteWelcomeContentProps) {
  const isTrial = quotaInfo?.period === "trial";
  const left = isTrial ? quotaInfo!.quotesLeft : 0;
  const fresh = isTrial && quotaInfo!.quotesUsed === 0;

  let quotaLine: ReactNode = null;
  if (isTrial) {
    if (left === 0) quotaLine = "You've used all of your free trial quotations. You can still explore everything below.";
    else quotaLine = <>You have <strong className="font-bold">{left} FREE {left === 1 ? "QUOTE" : "QUOTES"}</strong>{fresh ? " to get started." : " left."} Let's put them to good use!</>;
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6 space-y-5 sm:space-y-6 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500">
      <section className="relative overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 sm:p-7" data-testid="section-welcome-hero">
        <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full border-[14px] border-primary/10" />
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {firstName ? `Hello ${firstName}` : "Hello there"}
        </p>
        <h1 className="mt-1 text-xl sm:text-2xl font-bold leading-tight" data-testid="text-welcome-title">
          Welcome to The Proper Tool!
        </h1>
        <p className="mt-3 font-medium text-foreground">
          You've found The Proper Tool for the job.
        </p>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground">
          Good to have you here. Once you've seen how much time it could save on your quotes, we think you'll find it hard to go back to doing them the old way.
        </p>
        {quotaLine && (
          <div className="mt-4 inline-flex max-w-full items-start gap-2 rounded-lg bg-background/80 px-3 py-2 text-sm font-semibold ring-1 ring-primary/20" data-testid="text-trial-quotes-left">
            <span className="mt-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] text-primary">{left}</span>
            <span className="min-w-0">{quotaLine}</span>
          </div>
        )}
      </section>

      <section aria-labelledby="methods-heading" className="space-y-3">
        <h2 id="methods-heading" className="text-xs font-semibold uppercase tracking-wider text-primary">Three ways to quote</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {METHODS.map((m) => {
            const Icon = m.icon;
            const featured = "featured" in m && m.featured;
            return (
              <article
                key={m.id}
                data-testid={`card-method-${m.id}`}
                className={`rounded-xl border bg-card p-4 sm:p-5 ${featured ? "sm:col-span-2 border-primary/40 shadow-sm" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold leading-snug">
                      <span className="text-muted-foreground">{m.n}.</span> {m.title}
                    </h3>
                    <p className="text-sm font-medium text-primary">{m.tag}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{m.body}</p>
                {"tip" in m && m.tip && (
                  <p className="mt-3 rounded-md border-l-2 border-primary bg-primary/5 px-3 py-2 text-sm font-medium">{m.tip}</p>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <QuoteShowcase />

      <section className="rounded-xl border border-dashed border-primary/40 p-4 sm:p-5" data-testid="section-deal">
        <h2 className="flex items-center gap-2 font-semibold">
          <Handshake className="h-5 w-5 text-primary shrink-0" />
          Let's make a deal!
        </h2>
        {isTrial && left > 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Before you decide whether The Proper Tool is right for you, give {fresh && left === 5 ? "all five of your free quotes" : `your remaining ${plural(left)}`} a try. They're shared across Auto, Template and Manual, so try the different methods and see what suits the way you work. Give it a proper go and see how much time you could save. There's no rush: you can have a look around the dashboard first.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Try the different ways to quote and see what suits the way you work. Start a quote now or have a look around the dashboard first. It's up to you.
          </p>
        )}
        <p className="mt-2 text-sm font-medium">The more you use it, the more you'll see what it can do for your business.</p>
      </section>

      <nav aria-label="Get started" className="grid gap-2.5 sm:grid-cols-2">
        {LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <Link
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className={`group flex min-h-12 items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                l.primary
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border bg-card hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${l.primary ? "" : "text-primary"}`} />
              <span className="min-w-0 flex-1">{l.label}</span>
              <ArrowRight className="h-4 w-4 shrink-0 motion-safe:transition-transform group-hover:translate-x-0.5" />
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function QuoteWelcomePage() {
  const { user } = useAuth();
  const quotaInfo = getQuotaInfo(user);
  const firstName = user?.firstName;
  return (
    <>
      <Helmet>
        <title>Welcome | The Proper Tool</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <QuoteWelcomeContent quotaInfo={quotaInfo} firstName={firstName} />
    </>
  );
}
